// Ember's First Flight: bounded, serialisable rules. World/UI code uses atomic transactions here.
import { B } from "./blocks.js";
import { ITEMS, amount, addItem, STACK_MAX } from "./crafting.js";
export const cellKey = (b) => b.slice(0, 3).join(",");
export const DEVICE_IDS = new Set(
  [
    "door",
    "chest",
    "furnace",
    "bed",
    "sign_board",
    "rail",
    "powered_rail",
    "wire",
    "switch",
    "circuit_lamp",
    "drawbridge",
    "farm_plot",
    "perch",
    "enchant_table",
    "portal_frame",
  ].map((id) => B[id.toUpperCase()]),
);
export const LANDMARKS = {
  cave: { x: 37, z: 25, y: 41, name: "Crystal Mine entrance", icon: "⛏️" },
  vault: { x: 59, z: 26, y: 22, name: "Crystal Mine vault", icon: "💎" },
  farm: { x: 20, z: 20, y: 41, name: "Starter garden", icon: "🌱" },
  sky: { x: 48, z: 48, y: 69, name: "Ember’s Sky Garden", icon: "☁️" },
  moon: { x: 112, z: 48, y: 69, name: "Moonflower Island", icon: "🌙" },
  perch: { x: 7, z: -5, y: 41, name: "Ember’s perch", icon: "🐉" },
  railway: { x: -23, z: 4, y: 41, name: "Practice railway", icon: "🛤️" },
  machines: { x: -24, z: 23, y: 41, name: "Machine playground", icon: "💡" },
};
export const PUZZLES = {
  cave: {
    name: "The crystal door",
    icon: "💎",
    text: "Three mine carts carry 4 crystals each. How many crystals are there altogether?",
    choices: [7, 12, 16],
    answer: 12,
    hint: "Count 4 + 4 + 4. You can also use 3 × 4.",
    explain: "3 groups of 4 make 12. The crystal door opens!",
    reward: { crystal: 6, rail: 8 },
    flag: "cave",
  },
  flight: {
    name: "Ember’s sky map",
    icon: "🐉",
    text: "The sky gates show 5, 10, 15, __. Which number comes next?",
    choices: [16, 25, 20],
    answer: 20,
    hint: "Each gate adds 5. Count in fives.",
    explain: "15 + 5 = 20. Ember knows the route now!",
    reward: { crystal: 4 },
    flag: "flightLesson",
  },
  circuit: {
    name: "Power the secret door",
    icon: "💡",
    text: "A switch sends power along a wire to a lamp. What must the switch be to light the lamp?",
    choices: ["OFF", "ON", "It does not matter"],
    answer: "ON",
    hint: "An ON switch sends power. An OFF switch stops it.",
    explain:
      "The switch is ON. Power travels along the wire to light the lamp!",
    reward: { wire: 12, switch: 2, circuit_lamp: 2 },
    flag: "circuit",
  },
  farm: {
    name: "A garden for everyone",
    icon: "🌱",
    text: "Plant 3 equal rows with 4 seeds in each row. How many seeds do you need?",
    choices: [7, 12, 9],
    answer: 12,
    hint: "Draw 3 rows of 4 dots, then count them.",
    explain: "3 × 4 = 12 seeds. Try planting your own rows!",
    reward: { seeds: 12, farm_plot: 12 },
    flag: "farmLesson",
  },
  rail: {
    name: "The railway switch",
    icon: "🛤️",
    text: "The sign says: “Take the LEFT tunnel to the crystals.” Which way should the cart go?",
    choices: ["Left", "Right", "Back"],
    answer: "Left",
    hint: "The clue names the LEFT tunnel.",
    explain: "You followed the clue. The left tunnel leads to crystals.",
    reward: { powered_rail: 8 },
    flag: "railLesson",
  },
  frost: {
    name: "Frost enchantment",
    icon: "❄️",
    text: "Share 16 snowflakes equally between 4 ice gems. How many go in each?",
    choices: [2, 4, 8],
    answer: 4,
    hint: "4 + 4 + 4 + 4 = 16.",
    explain: "16 ÷ 4 = 4. Frost magic learned!",
    reward: {},
    flag: "frost",
  },
  fortune: {
    name: "Glow pickaxe enchantment",
    icon: "✨",
    text: "A crystal is worth 10 coins. You find 6 crystals. What is their total value?",
    choices: [16, 60, 106],
    answer: 60,
    hint: "Count six tens: 10, 20, 30, 40, 50, 60.",
    explain: "6 × 10 = 60. Treasure magic learned!",
    reward: {},
    flag: "fortune",
  },
};
// Lost & found: items that couldn't fit in the bag when the game moved or tidied something for the player.
// They wait here (never sold, never lost) and come back as soon as their stacks have room.
function cleanReturns(r) {
  const out = {};
  for (const [id, n] of Object.entries(r && typeof r === "object" ? r : {}).slice(0, 64))
    if (ITEMS[id] && ITEMS[id].kind !== "tool" && Number.isSafeInteger(n) && n > 0) out[id] = Math.min(n, 1e6);
  return out;
}
// Puts n of an item in the bag, keeping whatever doesn't fit in Lost & found. Returns how many reached the bag.
export function giveOrKeep(d, id, n) {
  if (!ITEMS[id] || !Number.isSafeInteger(n) || n <= 0) return 0;
  const cap = ITEMS[id].kind === "tool" ? 1 : STACK_MAX,
    got = Math.max(0, Math.min(n, cap - amount(d, id)));
  if (got) addItem(d, id, got);
  if (n > got && ITEMS[id].kind !== "tool") {
    const r = ((d.expansion ||= {}).returns ||= {});
    r[id] = Math.min(1e6, (r[id] || 0) + n - got);
  }
  return got;
}
// Moves Lost & found items into the bag as far as their stacks allow. Returns {got, left} item counts.
export function claimReturns(d) {
  const r = d.expansion?.returns || {};
  let got = 0,
    left = 0;
  for (const [id, n] of Object.entries(r)) {
    const room = Math.max(0, Math.min(n, STACK_MAX - amount(d, id)));
    if (room && addItem(d, id, room)) got += room;
    if (n - room > 0) {
      r[id] = n - room;
      left += n - room;
    } else delete r[id];
  }
  return { got, left };
}
export function ensureExpansion(d) {
  const a = d.expansion && typeof d.expansion === "object" ? d.expansion : {};
  const flags = {};
  for (const k of [
    "cave",
    "flightLesson",
    "circuit",
    "farmLesson",
    "railLesson",
    "frost",
    "fortune",
    "flew",
    "sky",
    "harvest",
    "railRide",
    "scout",
    "homeLesson",
    "intro",
    "bowGift", // 1.7: the Builder's gift (bow + 12 arrows) has been given, or a bow was already owned
  ])
    flags[k] = !!a.flags?.[k];
  const devices = {};
  const blocks = new Map((d.blocks || []).map((b) => [cellKey(b), b]));
  for (const [key, v] of Object.entries(a.devices || {}).slice(0, 512)) {
    const b = blocks.get(key);
    if (!b || !DEVICE_IDS.has(b[3]) || !v || typeof v !== "object") continue;
    const stash = {};
    for (const [id, n] of Object.entries(v.stash || {}).slice(0, 64))
      if (ITEMS[id] && Number.isSafeInteger(n) && n > 0)
        stash[id] = Math.min(n, ITEMS[id].kind === "tool" ? 1 : STACK_MAX);
    devices[key] = {
      on: !!v.on,
      turn: Number.isInteger(v.turn) ? ((v.turn % 4) + 4) % 4 : 0,
      text: String(v.text || "").slice(0, 40),
      stash,
      water: !!v.water,
      planted: Number.isFinite(v.planted)
        ? Math.max(0, Math.min(d.worldTime, v.planted))
        : null,
    };
  }
  const safePos = (p) =>
    Array.isArray(p) &&
    p.length === 3 &&
    p.every(Number.isFinite) &&
    Math.abs(p[0]) < 1e7 &&
    Math.abs(p[2]) < 1e7 &&
    p[1] >= 9 &&
    p[1] <= 96;
  d.expansion = {
    v: 1,
    flags,
    devices,
    home: safePos(a.home) ? a.home : null,
    gear: {
      armour:
        ["armour_iron", "armour_diamond"].includes(a.gear?.armour) &&
        amount(d, a.gear.armour)
          ? a.gear.armour
          : null,
      boots: !!a.gear?.boots && !!amount(d, "jump_boots"),
      shield: !!a.gear?.shield && !!amount(d, "shield"),
    },
    enchant: { frost: !!a.enchant?.frost, fortune: !!a.enchant?.fortune },
    animals: (Array.isArray(a.animals) ? a.animals : [])
      .filter(
        (v) =>
          v && ["sheep", "cow", "chicken"].includes(v.kind) && safePos(v.pos),
      )
      .slice(0, 16)
      .map((v, i) => ({
        id: i,
        kind: v.kind,
        pos: v.pos.slice(),
        baby: Math.max(0, Math.min(120, Number(v.baby) || 0)),
        fed: !!v.fed,
        ready: Math.max(0, Math.min(180, Number(v.ready) || 0)),
      })),
    scoutAt: Math.max(0, Number(a.scoutAt) || 0),
    flights: Math.max(0, Number(a.flights) || 0),
    harvests: Math.max(0, Number(a.harvests) || 0),
    adventures: Object.fromEntries(
      Object.entries(a.adventures || {}).filter(
        ([id, v]) => ["mine", "sky", "temple"].includes(id) && v === true,
      ),
    ),
    firstPerson: !!a.firstPerson,
    returns: cleanReturns(a.returns),
  };
  return d.expansion;
}
export function transact(save, cost = {}, gain = {}, coins = 0, gems = 0) {
  const d = save.discovery;
  if (
    !Number.isSafeInteger(coins) ||
    coins < 0 ||
    !Number.isSafeInteger(gems) ||
    gems < 0 ||
    save.coins < coins ||
    save.gems < gems
  )
    return false;
  const ids = new Set([...Object.keys(cost), ...Object.keys(gain)]);
  for (const id of ids) {
    const c = cost[id] || 0,
      g = gain[id] || 0,
      cap = ITEMS[id]?.kind === "tool" ? 1 : STACK_MAX;
    if (
      !ITEMS[id] ||
      ![c, g].every((n) => Number.isSafeInteger(n) && n >= 0) ||
      amount(d, id) < c ||
      amount(d, id) - c + g > cap
    )
      return false;
  }
  for (const id of ids)
    d.inventory.items[id] = amount(d, id) - (cost[id] || 0) + (gain[id] || 0);
  save.coins -= coins;
  save.gems -= gems;
  return true;
}
export function transferChest(d, key, id, n = 1, withdraw = false) {
  const dev = d.expansion.devices[key];
  if (
    !dev ||
    !d.blocks.some((b) => cellKey(b) === key && b[3] === B.CHEST) ||
    !ITEMS[id] ||
    ITEMS[id].kind === "tool" ||
    !Number.isSafeInteger(n) ||
    n < 1
  )
    return false;
  const stash = dev.stash || (dev.stash = {}),
    cap = ITEMS[id].kind === "tool" ? 1 : STACK_MAX;
  if (withdraw) {
    if ((stash[id] || 0) < n || amount(d, id) + n > cap) return false;
    stash[id] -= n;
    d.inventory.items[id] = amount(d, id) + n;
  } else {
    if (
      amount(d, id) < n ||
      (stash[id] || 0) + n > cap ||
      (!stash[id] &&
        Object.keys(stash).filter((k) => stash[k] > 0).length >= 64)
    )
      return false;
    d.inventory.items[id] -= n;
    stash[id] = (stash[id] || 0) + n;
  }
  return true;
}
export function deviceState(d, b) {
  const key = cellKey(b);
  if (!d.expansion.devices[key])
    d.expansion.devices[key] = {
      turn: 0,
      on: false,
      text: "", // an unwritten sign says whose home it is (expansion.js)
      stash: {},
      water: false,
      planted: null,
    };
  return d.expansion.devices[key];
}
export function canRemoveDevice(d, b) {
  const dev = d.expansion.devices[cellKey(b)];
  return (
    b[3] !== B.CHEST || !Object.values(dev?.stash || {}).some((n) => n > 0)
  );
}
export function solvePuzzle(save, id, answer) {
  const p = PUZZLES[id],
    a = save.discovery.expansion;
  if (!p || a.flags[p.flag] || answer !== String(p.answer)) return false;
  if (!transact(save, {}, p.reward)) return false;
  a.flags[p.flag] = true;
  save.coins += 20;
  save.gems += 1;
  save.stats.correct++;
  save.stats.answered++;
  save.stats.coinsTotal += 20;
  save.stats.gemsTotal++;
  return true;
}
export function circuitPower(blocks, devices) {
  const nodes = new Map(
    blocks
      .filter((b) =>
        [B.WIRE, B.SWITCH, B.CIRCUIT_LAMP, B.DOOR, B.DRAWBRIDGE].includes(b[3]),
      )
      .map((b) => [cellKey(b), b]),
  );
  const powered = new Set(),
    queue = blocks
      .filter((b) => b[3] === B.SWITCH && devices[cellKey(b)]?.on)
      .map(cellKey);
  while (queue.length && powered.size < 2048) {
    const k = queue.shift();
    if (powered.has(k) || !nodes.has(k)) continue;
    powered.add(k);
    const b = nodes.get(k);
    for (const [dx, dy, dz] of [
      [1, 0, 0],
      [-1, 0, 0],
      [0, 0, 1],
      [0, 0, -1],
      [0, 1, 0],
      [0, -1, 0],
    ]) {
      const q = [b[0] + dx, b[1] + dy, b[2] + dz].join(",");
      if (nodes.has(q) && !powered.has(q)) queue.push(q);
    }
  }
  return powered;
}
export function railNeighbours(blocks, b) {
  return blocks.filter(
    (q) =>
      [B.RAIL, B.POWERED_RAIL].includes(q[3]) &&
      Math.abs(q[0] - b[0]) + Math.abs(q[2] - b[2]) === 1 &&
      Math.abs(q[1] - b[1]) <= 1,
  );
}
// The last quest's title uses the player's name.
export function nextQuest(d, name) {
  const a = d.expansion,
    f = a.flags;
  if (!d.built)
    return {
      title: "Make a home for Scout",
      text: "Choose oak blocks. Build on the golden square. Your home saves!",
      target: { x: 0, z: 6, name: "Your building plot", icon: "🏡" },
    };
  if (!d.dragonHatched)
    return {
      title: "Wake your dragon",
      text: "Collect the six learning stamps. Each gem teaches something different.",
      target: null,
    };
  if (!f.cave)
    return {
      title: "Find Ember’s crystal treasure",
      text: "Follow the mine stairs. Solve the crystal door clue.",
      target: LANDMARKS.cave,
    };
  if (!d.blocks.some((b) => b[3] === B.PERCH))
    return {
      title: "Build Ember a perch",
      text: "Craft a dragon perch at the table, then place it beside your home.",
      target: { x: -10, z: -16, name: "Crafting table", icon: "🛠️" },
    };
  if (!amount(d, "saddle"))
    return {
      title: "Craft Ember’s saddle",
      text: "You need 4 hide, 4 wool and 4 crystals. Craft → Cool things.",
      target: { x: -10, z: -16, name: "Crafting table", icon: "🛠️" },
    };
  if (!f.flightLesson)
    return {
      title: "Learn the sky route",
      text: "Visit a dragon perch and solve Ember’s sky map.",
      target: LANDMARKS.perch,
    };
  if (!f.flew)
    return {
      title: "Your first dragon flight!",
      text: "Use a perch to ride Ember. Jump rises; Land brings you down.",
      target: LANDMARKS.perch,
    };
  if (!f.sky)
    return {
      title: "A garden above the clouds",
      text: "Fly to the floating island. Land beside its sparkling beacon.",
      target: LANDMARKS.sky,
    };
  if (a.harvests < 3)
    return {
      title: "Grow a little farm",
      text: "Plant seeds in garden beds, then water them. Harvest 3 crops.",
      target: LANDMARKS.farm,
    };
  if (!f.circuit)
    return {
      title: "Make a secret entrance",
      text: "Visit the machine playground to learn a two-switch puzzle.",
      target: LANDMARKS.machines,
    };
  if (!f.railRide)
    return {
      title: "Ride your own railway",
      text: "Craft rails and a minecart. Place a connected track, then Use a rail.",
      target: LANDMARKS.railway,
    };
  return {
    title: `${name}’s world is yours to invent`,
    text: "Build a castle, grow your farm, make machines or try a new Adventure.",
    target: null,
  };
}
